/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as api from './api';
import * as mainApi from '../../../api';
import { GamePhaseReflections, LoadStatus, LoadingState } from '../../../types';
import { NotificationEvent, ClassMembership, Classroom } from './types';
import { Player } from '../player/types';
import { GameStateData, LearningObjective, Room } from '../game/types';
import {
  addOrUpdateClass,
  addOrUpdateClassMembership,
  addOrUpdateGameRoom,
  addOrUpdateLearningObjective,
  removeGameRoom,
} from './helpers';
import * as gameRoomApi from '../../../hooks/game-rooms/game-room-api';

export interface StaticGame {
  id: string;
  name: string;
}

export interface EducationalDataStateData {
  classes: Classroom[];
  rooms: Room[];
  students: Player[];
  classMemberships: ClassMembership[];
  phaseReflections: GamePhaseReflections[];
  hydrationLoadStatus: LoadingState;
  gameList: StaticGame[];
  notifications: NotificationEvent[];
  learningObjectives: LearningObjective[];
}

const initialState: EducationalDataStateData = {
  classes: [],
  rooms: [],
  students: [],
  classMemberships: [],
  phaseReflections: [],
  learningObjectives: [],
  hydrationLoadStatus: { status: LoadStatus.NONE },
  gameList: [],
  notifications: [],
};

/** Actions */
export const fetchInstructorDataHydration = createAsyncThunk(
  'educationalData/fetchInstructorDataHydration',
  async () => {
    return await api.fetchInstructorDataHydration();
  }
);

export const fetchStudentDataHydration = createAsyncThunk(
  'educationalData/fetchStudentDataHydration',
  async () => {
    return await api.fetchStudentDataHydration();
  }
);

export const createClassroom = createAsyncThunk(
  'educationalData/createClassroom',
  async () => {
    return await api.createClassroom();
  }
);

export const createNewClassInviteCode = createAsyncThunk(
  'educationalData/createNewClassInviteCode',
  async (args: { classId: string; validUntil: Date; numUses: number }) => {
    return await api.createNewClassInviteCode(
      args.classId,
      args.validUntil,
      args.numUses
    );
  }
);

export const revokeClassInviteCode = createAsyncThunk(
  'educationalData/revokeClassInviteCode',
  async (args: { classId: string; classroomCode: string }) => {
    return await api.revokeClassInviteCode(args.classId, args.classroomCode);
  }
);

export const joinClassroom = createAsyncThunk(
  'educationalData/joinClassroom',
  async (args: { inviteCode: string }) => {
    return await api.joinClassroom(args.inviteCode);
  }
);

export const leaveClassroom = createAsyncThunk(
  'educationalData/leaveClassroom',
  async (args: { classId: string }) => {
    return await api.leaveClassroom(args.classId);
  }
);

export const removeStudentFromClass = createAsyncThunk(
  'educationalData/removeStudentFromClass',
  async (args: { studentId: string; classId: string }) => {
    return await api.removeStudentFromClass(args.studentId, args.classId);
  }
);

export const blockStudentFromClass = createAsyncThunk(
  'educationalData/blockStudentFromClass',
  async (args: { studentId: string; classId: string }) => {
    return await api.blockStudentFromClass(args.studentId, args.classId);
  }
);

export const unblockStudentFromClass = createAsyncThunk(
  'educationalData/unblockStudentFromClass',
  async (args: { studentId: string; classId: string }) => {
    return await api.unblockStudentFromClass(args.studentId, args.classId);
  }
);

export const assignStudentToGroup = createAsyncThunk(
  'educationalData/assignStudentToGroup',
  async (args: { studentId: string; classId: string; groupId: number }) => {
    return await api.assignStudentToGroup(
      args.studentId,
      args.classId,
      args.groupId
    );
  }
);

export const assignClassGroupsAndStart = createAsyncThunk(
  'educationalData/assignClassGroupsAndStart',
  async (args: { classId: string; groups: ClassMembership[] }) => {
    return await api.assignClassGroupsAndStart(args.classId, args.groups);
  }
);

export const copyAndArchiveClassroom = createAsyncThunk(
  'educationalData/copyAndArchiveClassroom',
  async (args: { classId: string }) => {
    return await api.copyAndArchiveClassroom(args.classId);
  }
);

export const adjustClassroomArchiveStatus = createAsyncThunk(
  'educationalData/adjustClassroomArchiveStatus',
  async (args: { classId: string; setArchived: boolean }) => {
    return await api.adjustClassroomArchiveStatus(
      args.classId,
      args.setArchived
    );
  }
);

export const updateClassNameDescription = createAsyncThunk(
  'educationalData/updateClassNameDescription',
  async (args: { classId: string; name: string; description: string }) => {
    return await api.updateClassNameDescription(
      args.classId,
      args.name,
      args.description
    );
  }
);

export const joinGameRoom = createAsyncThunk(
  'educationalData/joinGameRoom',
  async (args: { gameRoomId: string }): Promise<Room> => {
    return await gameRoomApi.joinGameRoom(args.gameRoomId);
  }
);

export const leaveGameRoom = createAsyncThunk(
  'educationalData/leaveGameRoom',
  async (args: { gameRoomId: string }): Promise<Room> => {
    return await gameRoomApi.leaveGameRoom(args.gameRoomId);
  }
);

export const deleteGameRoom = createAsyncThunk(
  'educationalData/deleteGameRoom',
  async (args: { gameRoomId: string }): Promise<Room> => {
    return await mainApi.deleteRoom(args.gameRoomId);
  }
);

export const renameGameRoom = createAsyncThunk(
  'educationalData/renameGameRoom',
  async (args: { gameRoomId: string; name: string }): Promise<Room> => {
    return await mainApi.renameGameRoom(args.name, args.gameRoomId);
  }
);

export const fetchRoom = createAsyncThunk(
  'educationalData/fetchRoom',
  async (args: { roomId: string }): Promise<Room> => {
    return await mainApi.fetchRoom(args.roomId);
  }
);

export const fetchRooms = createAsyncThunk(
  'educationalData/fetchRooms',
  async (args: { game: string }): Promise<Room[]> => {
    return await mainApi.fetchRooms(args.game);
  }
);

export const createNewGameRoom = createAsyncThunk(
  'educationalData/createNewGameRoom',
  async (args: {
    gameId: string;
    gameName: string;
    classId?: string;
  }): Promise<Room> => {
    return await gameRoomApi.createNewGameRoom(
      args.gameId,
      args.gameName,
      args.classId
    );
  }
);

export const updatePlayerGameStateData = createAsyncThunk(
  'educationalData/updatePlayerGameStateData',
  async (args: {
    roomId: string;
    playerId: string;
    gameStateData: GameStateData;
  }) => {
    return await gameRoomApi.updatePlayerGameStateData(
      args.roomId,
      args.playerId,
      args.gameStateData
    );
  }
);

export const sendMessageToGameRoom = createAsyncThunk(
  'educationalData/sendMessageToGameRoom',
  async (args: { roomId: string; message: string }) => {
    return await gameRoomApi.sendMessageToGameRoom(args.roomId, args.message);
  }
);

export const setPlayerPauseStatus = createAsyncThunk(
  'educationalData/setPlayerPauseStatus',
  async (args: { roomId: string; playerId: string; isPaused: boolean }) => {
    return await gameRoomApi.setPlayerPauseStatus(
      args.roomId,
      args.playerId,
      args.isPaused
    );
  }
);

export const shareClassroomWithInstructor = createAsyncThunk(
  'educationalData/shareClassroomWithInstructor',
  async (args: { classId: string; instructorId: string }) => {
    return await api.shareClassroomWithInstructor(
      args.classId,
      args.instructorId
    );
  }
);

export const assignGameToGameRoom = createAsyncThunk(
  'educationalData/assignGameToGameRoom',
  async (args: { roomId: string; gameId: string }) => {
    return await api.assignGameToGameRoom(args.roomId, args.gameId);
  }
);

export const setPlayerNeedsHelpInRoom = createAsyncThunk(
  'educationalData/setPlayerNeedsHelpInRoom',
  async (args: { roomId: string; needsHelp: boolean }) => {
    return await api.setPlayerNeedsHelpInRoom(args.roomId, args.needsHelp);
  }
);

export const fetchLearningObjectives = createAsyncThunk(
  'educationalData/fetchLearningObjectives',
  async () => {
    return await api.fetchLearningObjectives();
  }
);

export const createLearningObjective = createAsyncThunk(
  'educationalData/createLearningObjective',
  async (args: { learningObjective: Omit<LearningObjective, '_id'> }) => {
    return await api.createLearningObjective(args.learningObjective);
  }
);

export const updateLearningObjective = createAsyncThunk(
  'educationalData/updateLearningObjective',
  async (args: {
    learningObjectiveId: string;
    learningObjective: Omit<LearningObjective, '_id'>;
  }) => {
    return await api.updateLearningObjective(
      args.learningObjectiveId,
      args.learningObjective
    );
  }
);

export const dismissNotifications = createAsyncThunk(
  'educationalData/dismissNotifications',
  async () => {
    return await api.dismissNotifications();
  }
);

export const educationalDataSlice = createSlice({
  name: 'educationalData',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(dismissNotifications.fulfilled, (state, action) => {
        state.notifications = action.payload;
      })

      .addCase(fetchLearningObjectives.fulfilled, (state, action) => {
        state.learningObjectives = action.payload;
      })

      .addCase(createLearningObjective.fulfilled, (state, action) => {
        addOrUpdateLearningObjective(state, action.payload);
      })

      .addCase(updateLearningObjective.fulfilled, (state, action) => {
        addOrUpdateLearningObjective(state, action.payload);
      })

      .addCase(setPlayerPauseStatus.fulfilled, (state, action) => {
        addOrUpdateGameRoom(state, action.payload);
      })

      .addCase(updatePlayerGameStateData.fulfilled, (state, action) => {
        addOrUpdateGameRoom(state, action.payload);
      })

      .addCase(createNewGameRoom.fulfilled, (state, action) => {
        addOrUpdateGameRoom(state, action.payload);
      })

      .addCase(fetchRoom.fulfilled, (state, action) => {
        addOrUpdateGameRoom(state, action.payload);
      })

      .addCase(renameGameRoom.fulfilled, (state, action) => {
        addOrUpdateGameRoom(state, action.payload);
      })

      .addCase(deleteGameRoom.fulfilled, (state, action) => {
        removeGameRoom(state, action.payload);
      })

      .addCase(joinGameRoom.fulfilled, (state, action) => {
        addOrUpdateGameRoom(state, action.payload);
      })

      .addCase(leaveGameRoom.fulfilled, (state, action) => {
        removeGameRoom(state, action.payload);
      })

      .addCase(sendMessageToGameRoom.fulfilled, (state, action) => {
        addOrUpdateGameRoom(state, action.payload);
      })

      .addCase(fetchInstructorDataHydration.pending, (state) => {
        state.hydrationLoadStatus = {
          status: LoadStatus.IN_PROGRESS,
          startedAt: Date.now.toString(),
          error: undefined,
        };
      })
      .addCase(fetchInstructorDataHydration.rejected, (state, action) => {
        state.hydrationLoadStatus = {
          status: LoadStatus.FAILED,
          failedAt: Date.now.toString(),
          error: action.error.message,
        };
        state.classes = [];
        state.rooms = [];
        state.students = [];
        state.classMemberships = [];
        state.phaseReflections = [];
        state.gameList = [];
        state.notifications = [];
      })
      .addCase(fetchInstructorDataHydration.fulfilled, (state, action) => {
        state.classes = action.payload.classes;
        state.rooms = action.payload.rooms;
        state.students = action.payload.students;
        state.classMemberships = action.payload.classMemberships;
        state.phaseReflections = action.payload.phaseReflections;
        state.gameList = action.payload.gameList;
        state.notifications = action.payload.notifications;
        state.hydrationLoadStatus = {
          status: LoadStatus.DONE,
          endedAt: Date.now.toString(),
          error: undefined,
        };
      })

      .addCase(fetchStudentDataHydration.pending, (state) => {
        state.hydrationLoadStatus = {
          status: LoadStatus.IN_PROGRESS,
          startedAt: Date.now.toString(),
          error: undefined,
        };
      })
      .addCase(fetchStudentDataHydration.rejected, (state, action) => {
        state.classes = [];
        state.rooms = [];
        state.students = [];
        state.classMemberships = [];
        state.gameList = [];
        state.notifications = [];
        state.hydrationLoadStatus = {
          status: LoadStatus.FAILED,
          failedAt: Date.now.toString(),
          error: action.error.message,
        };
      })
      .addCase(fetchStudentDataHydration.fulfilled, (state, action) => {
        state.classes = action.payload.classes;
        state.rooms = action.payload.rooms;
        state.students = action.payload.students;
        state.classMemberships = action.payload.classMemberships;
        state.phaseReflections = action.payload.phaseReflections;
        state.gameList = action.payload.gameList;
        state.notifications = action.payload.notifications;
        state.hydrationLoadStatus = {
          status: LoadStatus.DONE,
          endedAt: Date.now.toString(),
          error: undefined,
        };
      })

      .addCase(createClassroom.fulfilled, (state, action) => {
        state.classes.push(action.payload);
      })

      .addCase(createNewClassInviteCode.fulfilled, (state, action) => {
        state.classes = state.classes.map((c) =>
          c._id === action.payload._id ? action.payload : c
        );
      })

      .addCase(revokeClassInviteCode.fulfilled, (state, action) => {
        state.classes = state.classes.map((c) =>
          c._id === action.payload._id ? action.payload : c
        );
      })

      .addCase(joinClassroom.fulfilled, (state, action) => {
        addOrUpdateClassMembership(state, action.payload.classMembership);
        addOrUpdateClass(state, action.payload.classroom);
      })

      .addCase(leaveClassroom.fulfilled, (state, action) => {
        addOrUpdateClassMembership(state, action.payload);
      })

      .addCase(removeStudentFromClass.fulfilled, (state, action) => {
        addOrUpdateClassMembership(state, action.payload);
      })

      .addCase(blockStudentFromClass.fulfilled, (state, action) => {
        addOrUpdateClassMembership(state, action.payload);
      })

      .addCase(unblockStudentFromClass.fulfilled, (state, action) => {
        addOrUpdateClassMembership(state, action.payload);
      })

      .addCase(assignStudentToGroup.fulfilled, (state, action) => {
        addOrUpdateClassMembership(state, action.payload);
      })

      .addCase(assignClassGroupsAndStart.fulfilled, (state, action) => {
        const { updatedClassroom, createdRooms } = action.payload;
        addOrUpdateClass(state, updatedClassroom);
        createdRooms.forEach((room) => {
          addOrUpdateGameRoom(state, room);
        });
      })
      .addCase(copyAndArchiveClassroom.fulfilled, (state, action) => {
        const { updatedClassroom, createdRooms } = action.payload;
        addOrUpdateClass(state, updatedClassroom);
        createdRooms.forEach((room) => {
          addOrUpdateGameRoom(state, room);
        });
      })

      .addCase(adjustClassroomArchiveStatus.fulfilled, (state, action) => {
        addOrUpdateClass(state, action.payload);
      })

      .addCase(updateClassNameDescription.fulfilled, (state, action) => {
        addOrUpdateClass(state, action.payload);
      })

      .addCase(shareClassroomWithInstructor.fulfilled, (state, action) => {
        addOrUpdateClass(state, action.payload);
      })

      .addCase(assignGameToGameRoom.fulfilled, (state, action) => {
        addOrUpdateGameRoom(state, action.payload);
      })

      .addCase(setPlayerNeedsHelpInRoom.fulfilled, (state, action) => {
        addOrUpdateGameRoom(state, action.payload);
      });
  },
});

export default educationalDataSlice.reducer;
